import React, { useMemo, useState } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import { LuPlus, LuSearch } from 'react-icons/lu';
import Loading from '../../shared/components/Loading/Loading';
import SidePanel from '../../shared/components/SidePanel/SidePanel';
import DateRangeFilter from '../../shared/components/DateRangeFilter/DateRangeFilter';
import Button from '../../shared/components/Button/Button';
import { useCoupons } from './hooks/useCoupons';
import { useCouponStats } from './hooks/useCouponStats';
import CouponStats from './components/CouponStats';
import CouponTable from './components/CouponTable';
import CouponForm from './components/CouponForm';
import './Coupons.css';

const STATUS_TABS = [
  { key: 'all', label: 'Todos' },
  { key: 'active', label: 'Ativos' },
  { key: 'inactive', label: 'Inativos' },
];

const CouponsPage = () => {
  const { coupons, loading, error, saveCoupon, removeCoupon } = useCoupons();
  const { stats, startDate, endDate, setStartDate, setEndDate, refetch: refetchStats } = useCouponStats();
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredCoupons = useMemo(() => {
    return coupons.filter((coupon) => {
      const matchesSearch = coupon.code.toLowerCase().includes(search.trim().toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || (statusFilter === 'active' ? coupon.enabled : !coupon.enabled);
      return matchesSearch && matchesStatus;
    });
  }, [coupons, search, statusFilter]);

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const openCreate = () => {
    setEditingCoupon(null);
    setPanelOpen(true);
  };

  const openEdit = (coupon) => {
    setEditingCoupon(coupon);
    setPanelOpen(true);
  };

  const handleSubmit = async (coupon) => {
    setSaving(true);
    try {
      await saveCoupon(coupon);
      toast.success('Cupom salvo com sucesso.', { autoClose: 4000 });
      setPanelOpen(false);
      await refetchStats();
    } catch (err) {
      toast.error('Não foi possível salvar o cupom.', { autoClose: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Excluir o cupom "${coupon.code}"?`)) return;
    try {
      await removeCoupon(coupon._id);
      toast.success('Cupom excluído.', { autoClose: 4000 });
      await refetchStats();
    } catch (err) {
      toast.error('Não foi possível excluir o cupom.', { autoClose: 4000 });
    }
  };

  return (
    <div className="site-content">
      <div className="content-wrapper pt-6 pb-6">
        <Container fluid>
          <Row className="align-items-center mb-4">
            <Col>
              <h3 className="mb-0">Cupons</h3>
              <p className="text-muted mb-0">Gerencie cupons de desconto e benefícios oferecidos aos clientes.</p>
            </Col>
            <Col xs="auto" className="d-flex align-items-center" style={{ gap: 12 }}>
              <DateRangeFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} />
              <Button variant="primary" className="d-flex align-items-center" onClick={openCreate}>
                <LuPlus size={18} className="mr-1" />
                Novo cupom
              </Button>
            </Col>
          </Row>

          {error && (
            <Row className="mb-4">
              <Col>
                <div className="alert alert-danger mb-0">Não foi possível carregar os cupons. Tente novamente.</div>
              </Col>
            </Row>
          )}

          <CouponStats stats={stats} totalCoupons={coupons.length} />

          <Row>
            <Col>
              <div className="coupons-panel">
                <div className="coupons-toolbar">
                  <div className="coupons-toolbar__search">
                    <LuSearch size={16} />
                    <input
                      type="text"
                      placeholder="Buscar cupom..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <div className="coupons-toolbar__tabs">
                    {STATUS_TABS.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        className={`coupons-toolbar__tab ${statusFilter === tab.key ? 'is-active' : ''}`}
                        onClick={() => setStatusFilter(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                <CouponTable
                  coupons={filteredCoupons}
                  perCoupon={stats?.perCoupon}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <SidePanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={editingCoupon ? 'Editar cupom' : 'Novo cupom'}
      >
        <CouponForm
          initialCoupon={editingCoupon}
          onSubmit={handleSubmit}
          onCancel={() => setPanelOpen(false)}
          saving={saving}
        />
      </SidePanel>

      {loading && <Loading overlay />}
    </div>
  );
};

export default CouponsPage;
